import { queryOptions, useQuery } from '@tanstack/react-query';
import { getAddress, isAddress } from 'viem';
import { z } from 'zod';

import { apiRequest } from '@/lib/api-client';
import { tokenSymbolField } from '@/lib/token-symbol';
import { tolerantRows } from '@/lib/tolerant-rows';

/**
 * The asset registry, keyed by address.
 *
 * A vault's `allocations[]` carry an asset address, a name and a weight, but
 * nothing about WHAT the asset is. The registry is where an address becomes
 * "Stablecoins" or "U.S. Treasury Funds", which is what the Opportunities
 * table needs to describe a vault's exposure without guessing from its name.
 *
 * Shared rather than feature-owned: the Composer reads the same endpoint for
 * its own selection step, and a second consumer is the point at which this
 * stops belonging to either of them.
 */

/**
 * One registry row. Thirteen screens read this map, so a row is refused only
 * when it cannot be keyed at all (no usable address). Everything else is
 * reference data: a field that is off is blanked on that row, and the row is
 * still served.
 */
const registryAssetSchema = z.object({
  // Any spelling of a 20-byte address is the same address; a wrong EIP-55
  // checksum is a casing mistake, not a different asset.
  address: z
    .string()
    .refine(
      (value) => isAddress(value, { strict: false }),
      'Expected an EVM asset address'
    )
    .transform((value) => getAddress(value)),
  /**
   * May be empty upstream. Null, not '', so a caller's `??` fallback (the
   * address, a placeholder) applies instead of printing a blank label.
   */
  symbol: z
    .string()
    .max(128)
    .nullish()
    .catch(null)
    .transform((value) => value || null),
  /**
   * The token's own `symbol()` (v1.160.0); `symbol` is the registry's
   * code. Screens print `displaySymbol(tokenSymbol, symbol)`.
   */
  tokenSymbol: tokenSymbolField('asset registry', 'assets.tokenSymbol'),
  name: z
    .string()
    .max(256)
    .nullish()
    .catch(null)
    .transform((value) => value || null),
  /** The asset's scale. Carried, never guessed: absent or unreadable is null. */
  decimals: z.number().int().min(0).max(255).nullish().catch(null),
  assetType: z.string().nullish().catch(null),
  /** A rating is a whole number or it is not a rating; never rounded. */
  riskRating: z.number().int().nullish().catch(null),
  category: z.string().nullish().catch(null),
  logoUrl: z.string().nullish().catch(null),
  liquidityTier: z.string().nullish().catch(null),
  issuer: z.string().nullish().catch(null),
  assetClass: z
    .object({ slug: z.string().min(1), displayName: z.string().min(1) })
    .nullish()
    .catch(null),
  issuerInfo: z
    .object({
      slug: z.string().min(1),
      name: z.string().min(1),
      logoUrl: z.string().nullish().catch(null)
    })
    .nullish()
    .catch(null)
});

export type RegistryAsset = z.infer<typeof registryAssetSchema>;

/** The registry's max page size since v1.62.0; larger is a 400. */
const PAGE_LIMIT = 100;

const registryRowsSchema = tolerantRows(registryAssetSchema, {
  label: 'asset registry',
  rejectWhenNoneSurvive: true
});

/**
 * One page. `served` is how many rows the page held before any were
 * dropped, so a dropped row cannot make the paging loop think the registry
 * is longer than it is.
 */
const registryPageSchema = z
  .object({
    assets: z.array(z.unknown()).max(PAGE_LIMIT),
    // Only steers paging. Without it the loop stops on the first short page.
    total: z.number().int().min(0).optional().catch(undefined)
  })
  .transform((page, context) => {
    const rows = registryRowsSchema.safeParse(page.assets);
    if (!rows.success) {
      context.addIssue({
        code: 'custom',
        path: ['assets'],
        message: 'No asset registry row could be read'
      });
      return z.NEVER;
    }
    return { assets: rows.data, served: page.assets.length, total: page.total };
  });

const assetRegistryKeys = {
  all: ['asset-registry'] as const,
  list: (chainId: number) => [...assetRegistryKeys.all, chainId] as const
};

/**
 * The whole registry for a chain, keyed by lowercase address. Shared by the
 * hook below and by the vault record's server read, so both name assets the
 * same way and both survive a bad row. Deliberately not a `'use client'`
 * module: a server component calling a client module's export gets a
 * client reference, not the function.
 */
export async function fetchAssetRegistry(
  chainId: number | undefined,
  signal?: AbortSignal
): Promise<Map<string, RegistryAsset>> {
  const byAddress = new Map<string, RegistryAsset>();
  let served = 0;
  // v1.62.0 paginated this endpoint with a default of 50. The registry is
  // small, but reading only the first page would silently mislabel every
  // asset past it as unclassified.
  for (let page = 1; page <= 20; page += 1) {
    const response = await apiRequest('/api/v1/assets', registryPageSchema, {
      // Public reference data: no wallet token. The chain is sent because
      // the cache entry is keyed by it; without it every chain's entry held
      // whatever the backend's default chain served.
      auth: 'none',
      chainId,
      query: { page, limit: PAGE_LIMIT },
      signal
    });
    for (const asset of response.assets) {
      byAddress.set(asset.address.toLowerCase(), asset);
    }
    served += response.served;
    if (response.served === 0) break;
    if (
      response.total != null
        ? served >= response.total
        : response.served < PAGE_LIMIT
    ) {
      break;
    }
  }
  return byAddress;
}

/**
 * Classification is protocol-level reference data, not per-user state — it
 * changes when an asset is onboarded, not while someone reads a table.
 *
 * The one cache entry for a chain's registry. A reader that needs the map
 * inside its own query (the vault record) takes it through this with
 * `fetchQuery`, so the page pages the registry once, not once per cache.
 */
export function assetRegistryQuery(chainId: number) {
  return queryOptions({
    queryKey: assetRegistryKeys.list(chainId),
    queryFn: ({ signal }) => fetchAssetRegistry(chainId, signal),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000
  });
}

export function useAssetRegistry(
  chainId: number,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    ...assetRegistryQuery(chainId),
    enabled: options.enabled ?? true
  });
}
